/// <amd-module name="Wiki/Editor/Components/EditorTextArea" />
import * as React from "react";

import { HtmlNormalizer } from "VSS/Features/Markdown/Utils/HtmlNormalizer";
import { IVssPageContext } from "VSS/Platform/Context";
import { css } from "VSS/Platform/Layout";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

import { WikiPage, WikiType } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { addAttachments } from "Wiki/Editor/AttachmentHelpers";
import { DragDropHelper, DropTarget } from "Wiki/Editor/Components/DropTarget";
import { IEditorActionCreator, IWikiEditorState } from "Wiki/Editor/Contracts";
import { isInsertTextSupported } from "Wiki/Editor/Helpers";
import * as Resources from "Wiki/Editor/Resources";
import { DragEventsSource } from "Wiki/ViewCommon/Constants";
import { IWikiMentionNwpWrapper, IWikiMentionNwpWrapperModule, IWikiTransferData, WikiPageInfoWithName } from "Wiki/ViewCommon/Contracts";
import { PageErrorType, TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getFileName, getLinkFromPath } from "Wiki/ViewCommon/Utils/PathHelper";
import { isIncludeEditingAllowed, publish } from "Wiki/ViewCommon/Utils/WikiHelper";

export interface IEditorTextAreaProps {
    className?: string;
    editorState: IWikiEditorState;
    actionCreator: IEditorActionCreator;
    getPagesForFiltering: () => Promise<WikiPage[]>;
    pageInfo: WikiPageInfoWithName;
    wikiType: WikiType;
    wikiMentionNwpModule: IWikiMentionNwpWrapperModule;
    textAreRef: React.RefObject<HTMLTextAreaElement>;
    onError: (message: string, errorType: PageErrorType) => void;
    pageContext: IVssPageContext;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
}

export class EditorTextArea extends React.PureComponent<IEditorTextAreaProps> {
    private _mentionEnhancement: IWikiMentionNwpWrapper | undefined;
    private _textAreaRef: React.RefObject<HTMLTextAreaElement>;

    // Following refs are required for auto complete.
    private _textAreaMirrorContainerRef = React.createRef<HTMLDivElement>();
    private _textAreaMirrorRef = React.createRef<HTMLSpanElement>();
    private _autoCompletePositioningRef = React.createRef<HTMLDivElement>();

    constructor(props: IEditorTextAreaProps) {
        super(props);
        this._textAreaRef = props.textAreRef ? props.textAreRef : React.createRef<HTMLTextAreaElement>();
    }

    public componentDidMount(): void {
        this._mentionEnhancement = new this.props.wikiMentionNwpModule.WikiMentionNwpWrapper(
            this._getTextAreaHTML(),
            this.props.getPagesForFiltering,
            this._getPagesToFilterExcludingCurrent,
            this._handleChangeByAutoComplete,
            this.props.wikiType,
            this._textAreaMirrorContainerRef.current!,
            this._textAreaMirrorRef.current!,
            this._autoCompletePositioningRef.current!,
            isIncludeEditingAllowed(this.props.pageContext)
        );
    }

    public componentDidUpdate(): void {
        if (!isInsertTextSupported(this.props.editorState.pageContent)) {
            // Edge Fix: Following if check is required for handling Edge issue as it auto focus on selection range change.
            if (
                this._getTextAreaHTML().selectionStart != this.props.editorState.selectionStart ||
                this._getTextAreaHTML().selectionEnd != this.props.editorState.selectionEnd
            ) {
                this._getTextAreaHTML().selectionStart = this.props.editorState.selectionStart;
                this._getTextAreaHTML().selectionEnd = this.props.editorState.selectionEnd;
            }
        }
    }

    public componentWillUnmount(): void {
        if (this._mentionEnhancement) {
            this._mentionEnhancement.dispose();
        }
    }

    public render(): JSX.Element {
        const textAreaClassName = "monospaced-text fontSize we-text flex-grow";
        return (
            <DropTarget className={css("we-ta-container flex-column", this.props.className)} onDrop={this._onDrop}>
                <textarea
                    className={textAreaClassName}
                    value={this.props.editorState.pageContent}
                    onChange={this._handleTextAreaChangeEvent}
                    onPaste={this._onPaste}
                    aria-label={Resources.EditorTextArea_AriaLabel}
                    ref={this._textAreaRef}
                    onKeyDown={this.props.onKeyDown}
                />
                <div ref={this._textAreaMirrorContainerRef} className={css("mirror-container", textAreaClassName)}>
                    <span ref={this._textAreaMirrorRef} />
                </div>
                <div ref={this._autoCompletePositioningRef} className={"ac-position"} />
            </DropTarget>
        );
    }

    private _addAttachments(files: File[]): void {
        this._getTextAreaHTML().focus();
        addAttachments(
            this.props.pageContext,
            files,
            this.props.actionCreator.addAttachments,
            this._getTextAreaHTML().selectionStart,
            this._getTextAreaHTML().selectionEnd,
            this.props.onError
        );
    }

    private _handleTextAreaChangeEvent = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        this._onTextChange(event.target.value);
    };

    private _handleChangeByAutoComplete = (): void => {
        this._onTextChange(this._getTextAreaHTML().value);
    };

    private _onTextChange = (text: string): void => {
        this.props.actionCreator.updatePageContent(text, this._getTextAreaHTML().selectionStart, this._getTextAreaHTML().selectionEnd);
    };

    private _onPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>): void => {
        const data: DataTransfer = event.clipboardData;
        const textPlain = "text/plain";
        const textHtml = "text/html";
        const image = "image/";
        const pasteObjectTypeOrder = [textPlain, textHtml, image];
        if (this.props.editorState.shouldPasteAsHtml) {
            pasteObjectTypeOrder[0] = textHtml;
            pasteObjectTypeOrder[1] = textPlain;
        }

        if (!data || !data.items) {
            return;
        }

        for (const pasteType of pasteObjectTypeOrder) {
            for (let itemIndex = 0; itemIndex < data.items.length; itemIndex++) {
                if (data.items[itemIndex].type.indexOf(pasteType) === 0) {
                    switch (pasteType) {
                        case textPlain:
                            return;
                        case textHtml:
                            this._pasteHtmlContent(event, data.items[itemIndex]);
                            return;
                        case image:
                            this._pasteImage(event, data.items[itemIndex]);
                            return;
                    }
                }
            }
        }
    };

    private _onDrop = (dataTransfer: DataTransfer): void => {
        if (DragDropHelper.isDragDataTypeFile(dataTransfer)) {
            const fileArray: File[] = DragDropHelper.getFileArrayFromDataTransfer(dataTransfer);
            publish(this.props.pageContext, TelemetryConstants.FilesDraggedAndDroppedToEditRegion, this.props.wikiType);
            this._addAttachments(fileArray);
        } else if (DragDropHelper.isDragDataTypeString(dataTransfer)) {
            try {
                const data = JSON.parse(dataTransfer.getData("text")) as IWikiTransferData;
                if (data.source === DragEventsSource.WikiTreeNode) {
                    publish(this.props.pageContext, TelemetryConstants.PageDraggedAndDroppedToEditRegion, this.props.wikiType);
                    this._getTextAreaHTML().focus();
                    const link = `[${getFileName(data.text)}](${getLinkFromPath(data.text)})`;
                    this.props.actionCreator.insertTextProgramatically(
                        link,
                        this._getTextAreaHTML().selectionStart,
                        this._getTextAreaHTML().selectionEnd,
                        true
                    );
                }
            } catch (e) {
                // Dropped item is not wiki tree node, Ignore.
            }
        }
    };

    private _pasteImage(event: React.ClipboardEvent<HTMLTextAreaElement>, item: DataTransferItem): void {
        event.stopPropagation();
        event.preventDefault();
        const file: File | null = item.getAsFile();
        if (file) {
            publish(this.props.pageContext, TelemetryConstants.ImagePastedFromClipboard, this.props.wikiType);
            this._addAttachments([file]);
        }
    }

    private _pasteHtmlContent(event: React.ClipboardEvent<HTMLTextAreaElement>, item: DataTransferItem): void {
        event.stopPropagation();
        event.preventDefault();
        item.getAsString((htmlContent: string) => {
            this._getTextAreaHTML().focus();
            if (htmlContent) {
                publish(this.props.pageContext, TelemetryConstants.HtmlPastedFromClipboard, this.props.wikiType);
                this.props.actionCreator.insertTextProgramatically(
                    this._getHtmlContentBody(htmlContent),
                    this._getTextAreaHTML().selectionStart,
                    this._getTextAreaHTML().selectionEnd
                );
            }
        });
    }

    private _getHtmlContentBody(htmlContent: string): string {
        let extractedHtmlContent: string = htmlContent;
        const startFragment = "<!--StartFragment-->";
        const endFragment = "<!--EndFragment-->";

        if (extractedHtmlContent.indexOf(startFragment) !== -1) {
            extractedHtmlContent = extractedHtmlContent.substring(
                htmlContent.indexOf(startFragment) + startFragment.length,
                htmlContent.indexOf(endFragment)
            );
        }

        const trimmedContent: string = extractedHtmlContent.trim();
        return HtmlNormalizer.normalizeStripAttributes(trimmedContent);
    }

    private _getTextAreaHTML = (): HTMLTextAreaElement => {
        return this._textAreaRef.current!;
    };

    private _getPagesToFilterExcludingCurrent = (): Promise<WikiPage[]> => {
        const { parentPath, pageName } = this.props.pageInfo;
        const currentPagePath = combineUrlPaths(parentPath, pageName);
        return this.props.getPagesForFiltering().then(allPages => allPages.filter(page => page.path !== currentPagePath));
    };
}
